import React, { useState, useRef, useEffect } from "react";
import WordImage from "./WordImage";

const whoSaidItImages = {
  bg: {
    male: "/images/words/bg/mazh.webp",
    female: "/images/words/bg/zhena.webp"
  },
  de: {
    male: "/images/words/de/journalist.webp",
    female: "/images/words/de/journalistin.webp"
  },
  cs: {
    male: "/images/words/cs/on.webp",
    female: "/images/words/cs/ona.webp"
  }
};

// Mapping of male/female names for each language
const whoSaidItNames = {
  de: {
    male: ["Tom", "Alex", "Niklas", "Nico", "Sascha"],
    female: ["Anna", "Elisa", "Lucy", "Maria", "Ina"]
  },
  cs: {
    male: ["Tomáš", "Aleš", "Nikola", "Niki", "Saša", "Alex"],
    female: ["Anička", "Eliška", "Marie", "Lucie", "Iva"]
  },
  bg: {
    male: ["Тома", "Алекс", "Никола", "Ники", "Сашо"],
    female: ["Ани", "Мария", "Люси", "Ели", "Ива"]
  }
};

const Listening = ({ language, labels, quizData, audioFile }) => {
  const [audioPlayed, setAudioPlayed] = useState(false);
  const [answers, setAnswers] = useState({});
  const [scoreSaved, setScoreSaved] = useState(false);
  const audioRef = useRef();

  // Find the quiz for the current language
  const quiz = quizData.find(q => q.language === language);

  // Calculate score and save to profile when quiz is completed
  useEffect(() => {
    if (
      quiz &&
      audioPlayed &&
      Object.keys(answers).length === quiz.questions.length &&
      !scoreSaved
    ) {
      // Calculate score
      let score = 0;
      quiz.questions.forEach((q, idx) => {
        if (answers[idx] === q.answer) score++;
      });
      // Save to localStorage profile
      try {
        const profile = JSON.parse(localStorage.getItem("blitzlesenUser") || "{}" );
        if (profile && profile.username) {
          if (!profile.scores) profile.scores = {};
          if (!profile.scores.listening) profile.scores.listening = [];
          profile.scores.listening.push({ score, timestamp: new Date().toISOString() });
          localStorage.setItem("blitzlesenUser", JSON.stringify(profile));
        }
      } catch (e) { /* ignore */ }
      setScoreSaved(true);
    }
    if (quiz && Object.keys(answers).length !== quiz.questions.length) {
      setScoreSaved(false);
    }
  }, [audioPlayed, answers, quiz, scoreSaved]);

  // Calculate current score for result display
  const totalQuestions = quiz ? quiz.questions.length : 0;
  const correctCount = quiz ? quiz.questions.reduce((acc, q, idx) => acc + (answers[idx] === q.answer ? 1 : 0), 0) : 0;
  const allAnswered = audioPlayed && Object.keys(answers).length === totalQuestions;

  if (!quiz) return <div>No quiz available for this language.</div>;

  // Helper to get image options for multiple_choice_image
  const getImageOptions = (question) => {
    // Use the same logic as MultipleChoice.js for mapping word to image
    return question.image_options.map(opt => ({
      word: opt,
      image: <WordImage word={opt} language={language} category={"multiple_choice"} maxWidthPct={null} style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 12, border: '2px solid #ccc' }} />
    })).filter(optObj => !!optObj.image);
  };

  // Handle answer selection
  const handleAnswer = (qIdx, value) => {
    setAnswers(prev => ({ ...prev, [qIdx]: value }));
  };

  return (
    <div style={{ maxWidth: 600, margin: "0 auto", padding: 24 }}>
      <h2>{labels.listening || "Listening"}</h2>
      <audio
        ref={audioRef}
        controls
        src={audioFile}
        onEnded={() => setAudioPlayed(true)}
        style={{ width: "100%", marginBottom: 24 }}
      >
        Your browser does not support the audio element.
      </audio>
      {!audioPlayed && (
        <div style={{ color: '#888', fontSize: '1.2em', margin: '24px 0' }}>
          {labels.listenToStory || "Listen to the story. The quiz will appear after playback."}
        </div>
      )}
      {audioPlayed && (
        <div style={{ marginTop: 32 }}>
          <h3>{labels.quiz || "Quiz"}</h3>
          {quiz.questions.map((q, idx) => {
            if (q.type === "multiple_choice_image") {
              const options = getImageOptions(q);
              return (
                <div key={idx} style={{ marginBottom: 32 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>{q.question}</div>
                  <div className="options-grid responsive-options-grid" style={{ gap: 4 }}>
                    {options.map((opt, i) => {
                      const isSelected = answers[idx] === opt.word;
                      const isCorrect = q.answer === opt.word;
                      let className = "";
                      if (answers[idx]) {
                        if (isSelected && isCorrect) className = "correct-answer";
                        else if (isSelected && !isCorrect) className = "wrong-answer";
                      }
                      return (
                        <button
                          key={opt.word}
                          className={className}
                          onClick={() => handleAnswer(idx, opt.word)}
                          disabled={!!answers[idx]}
                          style={{ margin: 0, border: 'none', background: 'none', padding: 0 }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            {React.cloneElement(opt.image, { style: { width: 90, height: 90, objectFit: 'cover', borderRadius: 12, border: '2px solid #ccc' } })}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {answers[idx] && (
                    <div style={{ marginTop: 8, color: answers[idx] === q.answer ? '#4caf50' : '#f44336' }}>
                      {answers[idx] === q.answer ? (labels.correctRead || "Correct!") : (labels.tryAgain || "Try again!")}
                    </div>
                  )}
                </div>
              );
            }
            if (q.type === "true_false") {
              return (
                <div key={idx} style={{ marginBottom: 32 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>{q.question}</div>
                  <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
                    <button
                      className={answers[idx] === true ? (q.answer === true ? "correct-answer" : "wrong-answer") : ""}
                      onClick={() => handleAnswer(idx, true)}
                      disabled={answers[idx] !== undefined}
                      style={{ fontSize: '2em' }}
                    >✅</button>
                    <button
                      className={answers[idx] === false ? (q.answer === false ? "correct-answer" : "wrong-answer") : ""}
                      onClick={() => handleAnswer(idx, false)}
                      disabled={answers[idx] !== undefined}
                      style={{ fontSize: '2em' }}
                    >❌</button>
                  </div>
                  {answers[idx] !== undefined && (
                    <div style={{ marginTop: 8, color: answers[idx] === q.answer ? '#4caf50' : '#f44336' }}>
                      {answers[idx] === q.answer ? (labels.correctRead || "Correct!") : (labels.tryAgain || "Try again!")}
                    </div>
                  )}
                </div>
              );
            }
            if (q.type === "who_said_it") {
              const whoImages = whoSaidItImages[language];
              const nameMap = whoSaidItNames[language] || { male: [], female: [] };
              return (
                <div key={idx} style={{ marginBottom: 32 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>{q.quote}</div>
                  <div style={{ display: 'flex', gap: 24, justifyContent: 'center', alignItems: 'center' }}>
                    {q.options.map((opt, i) => {
                      // Generalized mapping for male/female
                      let imgSrc = whoImages.female;
                      if (nameMap.male.includes(opt)) imgSrc = whoImages.male;
                      else if (nameMap.female.includes(opt)) imgSrc = whoImages.female;
                      return (
                        <button
                          key={opt}
                          onClick={() => handleAnswer(idx, opt)}
                          disabled={!!answers[idx]}
                          className={answers[idx] === opt ? (opt === q.answer ? "correct-answer" : "wrong-answer") : ""}
                          style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                        >
                          <img src={imgSrc} alt={opt} style={{ width: 90, height: 90, borderRadius: 12, border: answers[idx] === opt ? (opt === q.answer ? '3px solid #4caf50' : '3px solid #f44336') : '2px solid #ccc', boxShadow: answers[idx] === opt ? '0 0 10px #aaa' : 'none', objectFit: 'cover' }} />
                          <div style={{ textAlign: 'center', marginTop: 8 }}>{opt}</div>
                        </button>
                      );
                    })}
                  </div>
                  {answers[idx] && (
                    <div style={{ marginTop: 8, color: answers[idx] === q.answer ? '#4caf50' : '#f44336' }}>
                      {answers[idx] === q.answer ? (labels.correctRead || "Correct!") : (labels.tryAgain || "Try again!")}
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })}
          {/* Show result summary after all questions are answered */}
          {allAnswered && (
            <div style={{ marginTop: 32, textAlign: 'center', fontSize: '1.5em', fontWeight: 600, color: correctCount === totalQuestions ? '#4caf50' : (correctCount >= totalQuestions/2 ? '#1976d2' : '#f44336') }}>
              {labels.result || 'Result'}: {correctCount} / {totalQuestions} {labels.correctRead || 'correct!'}
              {correctCount === totalQuestions && <div>🎉 {labels.amazing || 'Amazing!'}</div>}
              {correctCount >= totalQuestions/2 && correctCount < totalQuestions && <div>😊 {labels.good || 'Good job!'}</div>}
              {correctCount < totalQuestions/2 && <div>😢 {labels.tryAgain || 'Try again!'}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Listening; 